import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { BusinessKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import isEqual from 'lodash-es/isEqual';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Button, Text } from '@frontend/design-system';
import { useAIRSettingsDefaultSources } from '../../../../contexts/air-settings-default-sources.context';
import { useAIRBusinessKnowledgeStore } from '../../../../stores/air-settings.store';
import { BusinessAddressSourceValue, BusinessNameSourceValue } from '../../../../types/air-settings.types';
import { AddCustomDetails } from '../../common/add-custom-details';
import { KnowledgeFormSection } from '../../common/knowledge-form-section';
import { BusinessAddressInput } from './business-address-input';
import { BusinessHoursInput } from './business-hours-input';
import { BusinessNameInput } from './business-name-input';
import { BusinessWebsiteInput } from './business-website-input';
import { FaqsInput } from './faqs-input';
import { useBusinessKnowledgeForm } from './use-business-knowledge-form';

type Props = {
  locationId: string;
  isManageAll?: boolean;
  presentFields?: Partial<Record<keyof BusinessKnowledge, boolean>>;
};

export const BusinessKnowledgeForm = ({ locationId, isManageAll = false, presentFields }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { locationDefaultSourcesMap } = useAIRSettingsDefaultSources();
  const { initialSettings, mutateDraft, markFieldAsDeleted } = useAIRBusinessKnowledgeStore([
    'initialSettings',
    'mutateDraft',
    'markFieldAsDeleted',
  ]);

  const [selectedOptionalFields, setSelectedOptionalFields] = useState<(keyof BusinessKnowledge)[]>([]);

  const {
    getFieldProps,
    values,
    settingsData,
    fieldErrors,
    hasBusinessAddressField,
    hasBusinessWebsiteField,
    hasBusinessWebsiteFaqField,
    touchedFields,
  } = useBusinessKnowledgeForm({ locationId, isManageAll });

  const initialData = useMemo(() => {
    return initialSettings[locationId] || {};
  }, [initialSettings, locationId]);

  const isFieldDirty = useCallback(
    (field: keyof BusinessKnowledge) => {
      return !isEqual(settingsData[field], initialData[field]);
    },
    [settingsData, initialData]
  );

  const businessNameSources = locationDefaultSourcesMap[locationId]?.names as BusinessNameSourceValue[];
  const businessAddressSources = locationDefaultSourcesMap[locationId]?.addresses as BusinessAddressSourceValue[];

  const handleDeleteField = (field: keyof BusinessKnowledge) => {
    setSelectedOptionalFields((prev) => prev.filter((f) => f !== field));
    markFieldAsDeleted(locationId, field);
  };

  const handleAddField = <T extends keyof BusinessKnowledge>(field: T, value: BusinessKnowledge[T]) => {
    setSelectedOptionalFields((prev) => [...prev, field]);
    mutateDraft(locationId, (draft) => {
      draft[field] = value;
    });
  };

  useEffect(() => {
    const fieldsToAdd: (keyof BusinessKnowledge)[] = [];
    if (hasBusinessAddressField) fieldsToAdd.push('addressOverride');
    if (hasBusinessWebsiteField) fieldsToAdd.push('businessWebsiteLink');
    if (hasBusinessWebsiteFaqField) fieldsToAdd.push('businessWebsiteFaqLink');

    setSelectedOptionalFields((prev) => {
      const newFields = fieldsToAdd.filter((f) => !prev.includes(f));
      return newFields.length > 0 ? [...prev, ...newFields] : prev;
    });
  }, [hasBusinessAddressField, hasBusinessWebsiteField, hasBusinessWebsiteFaqField]);

  return (
    <div css={styles.container}>
      {/* Business Name */}
      <KnowledgeFormSection
        title={t('Business Name')}
        description={t('How do you want AI Receptionist to introduce your business?')}
        isDirty={isFieldDirty('name')}
        hasError={!!touchedFields[locationId]?.name && !!fieldErrors[locationId]?.name}
      >
        <BusinessNameInput
          sourceFieldProps={getFieldProps('businessNameSource')}
          customValueFieldProps={getFieldProps('businessNameCustomValue')}
          sourceList={businessNameSources}
          isBulkEdit={isManageAll}
          showBulkOverrideWarningAlert={isManageAll && !!presentFields?.name}
        />
      </KnowledgeFormSection>
      {/* Business Hours */}
      <KnowledgeFormSection
        title={t('Business Hours')}
        description={t('What days and times is your business open?')}
        isDirty={isFieldDirty('businessHours')}
        hasError={!!touchedFields[locationId]?.businessHours && !!fieldErrors[locationId]?.businessHours}
      >
        <BusinessHoursInput
          getFieldProps={getFieldProps}
          values={values}
          showBulkOverrideWarningAlert={isManageAll && !!presentFields?.businessHours}
        />
      </KnowledgeFormSection>

      {selectedOptionalFields.map((field) => {
        switch (field) {
          case 'addressOverride':
            return (
              <KnowledgeFormSection
                key='addressOverride'
                title={t('Business Address')}
                description={t('Where is your business located?')}
                isDirty={isFieldDirty('addressOverride')}
                hasError={!!touchedFields[locationId]?.addressOverride && !!fieldErrors[locationId]?.addressOverride}
                onDelete={() => handleDeleteField('addressOverride')}
              >
                <BusinessAddressInput
                  getFieldProps={getFieldProps}
                  sourceList={businessAddressSources}
                  isBulkEdit={isManageAll}
                  showBulkOverrideWarningAlert={isManageAll && !!presentFields?.addressOverride}
                />
              </KnowledgeFormSection>
            );
          case 'businessWebsiteLink':
            return (
              <KnowledgeFormSection
                key='businessWebsiteLink'
                title={t('Business Website')}
                description={t('What is your website?')}
                note={t(
                  'The AI Receptionist will only share this link. It cannot read or use anything on the page itself.'
                )}
                isDirty={isFieldDirty('businessWebsiteLink')}
                hasError={
                  !!touchedFields[locationId]?.businessWebsiteLink && !!fieldErrors[locationId]?.businessWebsiteLink
                }
                onDelete={() => handleDeleteField('businessWebsiteLink')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.businessWebsiteLink}
              >
                <BusinessWebsiteInput fieldProps={getFieldProps('businessWebsiteLink')} />
              </KnowledgeFormSection>
            );
          case 'businessWebsiteFaqLink':
            return (
              <KnowledgeFormSection
                key='businessWebsiteFaqLink'
                title={t('Link To FAQs')}
                description={t('Where can your FAQ page be found?')}
                note={t(
                  'The AI Receptionist will only share this link. It cannot read or use anything on the page itself.'
                )}
                isDirty={isFieldDirty('businessWebsiteFaqLink')}
                hasError={
                  !!touchedFields[locationId]?.businessWebsiteFaqLink &&
                  !!fieldErrors[locationId]?.businessWebsiteFaqLink
                }
                onDelete={() => handleDeleteField('businessWebsiteFaqLink')}
                showBulkOverrideWarningAlert={isManageAll && !!presentFields?.businessWebsiteFaqLink}
              >
                <FaqsInput fieldProps={getFieldProps('businessWebsiteFaqLink')} />
              </KnowledgeFormSection>
            );
          default:
            return null;
        }
      })}

      {/* Footer Add Options */}
      {(!hasBusinessAddressField || !hasBusinessWebsiteField || !hasBusinessWebsiteFaqField) && (
        <div css={styles.addFieldsContainer}>
          <Divider css={{ margin: '0 0 var(--fabric-space-gap-lg) 0' }} />
          <div css={styles.chipsSection}>
            <Text color='subdued' size='medium'>
              {t('Click to add more details to this page')}
            </Text>
            <div
              css={{
                display: 'flex',
                gap: 'var(--fabric-space-gap-sm)',
              }}
            >
              {!hasBusinessAddressField && (
                <Button
                  variant='secondary'
                  size='small'
                  iconName='plus-small'
                  onClick={() => handleAddField('addressOverride', {})}
                >
                  {t('Business Address')}
                </Button>
              )}
              {!hasBusinessWebsiteField && (
                <Button
                  variant='secondary'
                  iconName='plus-small'
                  size='small'
                  onClick={() => handleAddField('businessWebsiteLink', '')}
                >
                  {t('Business Website')}
                </Button>
              )}
              {!hasBusinessWebsiteFaqField && (
                <Button
                  variant='secondary'
                  iconName='plus-small'
                  size='small'
                  onClick={() => handleAddField('businessWebsiteFaqLink', '')}
                >
                  {t('FAQs')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {!isManageAll && <AddCustomDetails />}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xl);
  `,
  addFieldsContainer: css`
    display: flex;
    flex-direction: column;
  `,
  chipsSection: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
  `,
};
